import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { AlertCircle, FileText, Calendar, Phone, MessageSquare } from 'lucide-react';

const ProblemSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const problems = [
    {
      icon: FileText,
      title: 'Scattered Records',
      description: 'Medical records spread across different hospitals, clinics, and apps'
    },
    {
      icon: Calendar,
      title: 'Missed Appointments',
      description: 'Hard to track multiple appointments for different family members'
    },
    {
      icon: AlertCircle,
      title: 'Medication Chaos',
      description: 'Managing prescriptions and dosages for children, adults, and elders'
    },
    {
      icon: Phone,
      title: 'Fragmented Care',
      description: 'Different apps for pharmacy, diagnostics, and consultations'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <section ref={ref} className="relative py-32 px-6 lg:px-8 bg-slate-950 overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
            The Problem with
            <span className="block mt-2 bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
              Family Healthcare Today
            </span>
          </h2>
          <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Managing health for multiple family members shouldn't feel like a full-time job
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
        >
          {problems.map((problem, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ scale: 1.02, y: -5 }}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-teal-500/10 to-cyan-500/10 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
              <div className="relative bg-slate-900/50 backdrop-blur-sm border border-slate-800 group-hover:border-teal-500/50 rounded-2xl p-8 transition-all duration-300">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500/20 to-cyan-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <problem.icon className="h-7 w-7 text-teal-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">{problem.title}</h3>
                <p className="text-slate-400 leading-relaxed">{problem.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center mt-20"
        >
          <div className="inline-flex items-center gap-3 bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-full px-8 py-4">
            <MessageSquare className="h-6 w-6 text-teal-400" />
            <p className="text-white font-semibold text-lg">We built Clan Health to solve this</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemSection;